import { useCallback, useEffect, useOptimistic, useState, useTransition } from 'react'
import { extractProjectName } from '@/common/utils'
import { BLOCKING_CONVERSATION_EVENT_TYPES_STRING } from '@/features/events/shared'
import type { ConversationEventPayload } from '@/infra/sse/conversation/payloads'
import { CLAUDE_CODE_CONVERSATION_EVENT } from '@/infra/sse'
import { env } from '@/infra/config/env'

export type SystemNotificationPermission = 'default' | 'granted' | 'denied'

export interface SystemNotificationOptions {
  readonly title: string
  readonly body: string
  readonly tag: string
  readonly requireInteraction?: boolean
  readonly silent?: boolean
  readonly data?: unknown
}

export interface SystemNotificationState {
  readonly isSupported: boolean
  readonly permission: SystemNotificationPermission
  readonly isRequesting: boolean
  readonly isEnabled: boolean
}

export interface SystemNotificationsValue {
  readonly state: SystemNotificationState
  readonly isPending: boolean
  readonly requestPermission: () => Promise<void>
  readonly showNotification: (options: Omit<SystemNotificationOptions, 'tag'>) => Promise<void>
  readonly testNotification: () => Promise<void>
  readonly checkPermission: () => void
}

export function useSystemNotifications(): SystemNotificationsValue {
  const isSupported = typeof globalThis !== 'undefined' && 'Notification' in globalThis

  const [state, setState] = useState<SystemNotificationState>({
    isSupported,
    permission: isSupported ? (Notification.permission as SystemNotificationPermission) : 'denied',
    isRequesting: false,
    isEnabled: isSupported && Notification.permission === 'granted',
  })

  const [isPending, startTransition] = useTransition()

  const [optimisticState, setOptimisticState] = useOptimistic(
    state,
    (
      currentState: SystemNotificationState,
      optimisticUpdate: Partial<SystemNotificationState>,
    ) => ({
      ...currentState,
      ...optimisticUpdate,
    }),
  )

  const checkPermission = useCallback((): void => {
    if (!isSupported) return

    try {
      const currentPermission = Notification.permission as SystemNotificationPermission
      startTransition(() => {
        setState((prev) => ({
          ...prev,
          permission: currentPermission,
          isEnabled: currentPermission === 'granted',
          isRequesting: false,
        }))
      })
    } catch {
      /* ignored */
    }
  }, [isSupported, startTransition])

  const showWelcomeNotification = useCallback(async (): Promise<void> => {
    if (!isSupported || state.permission !== 'granted') return

    try {
      const notification = new Notification('C4', {
        body: 'System notifications enabled!\n\nYou will now receive Claude Code events.',
        icon: '/vite.svg',
        tag: 'claude-welcome',
        requireInteraction: false,
        silent: false,
      })

      notification.onclick = () => {
        globalThis.focus()
        notification.close()
      }

      setTimeout(() => {
        notification.close()
      }, env.NOTIFICATION_WELCOME_AUTO_CLOSE_MS)
    } catch {
      /* ignored */
    }
  }, [isSupported, state.permission])

  const requestPermission = useCallback(async (): Promise<void> => {
    if (!isSupported || state.permission === 'granted') return

    startTransition(async () => {
      setOptimisticState({ isRequesting: true })

      setState((prev) => ({
        ...prev,
        isRequesting: true,
      }))

      try {
        const permission = (await Notification.requestPermission()) as SystemNotificationPermission

        setState((prev) => ({
          ...prev,
          permission,
          isEnabled: permission === 'granted',
          isRequesting: false,
        }))

        if (permission === 'granted') {
          await showWelcomeNotification()
        }
      } catch {
        setState((prev) => ({
          ...prev,
          isRequesting: false,
        }))
      }
    })
  }, [isSupported, state.permission, setOptimisticState, startTransition, showWelcomeNotification])

  const showNotification = useCallback(
    async (options: Omit<SystemNotificationOptions, 'tag'>): Promise<void> => {
      if (!isSupported || state.permission !== 'granted') return

      try {
        const notification = new Notification(options.title, {
          body: options.body,
          icon: '/vite.svg',
          tag: `claude-${Date.now()}`,
          requireInteraction: options.requireInteraction ?? false,
          silent: options.silent ?? false,
          data: options.data,
        })

        notification.onclick = () => {
          globalThis.focus()
          notification.close()
        }

        setTimeout(() => {
          notification.close()
        }, env.NOTIFICATION_AUTO_CLOSE_MS)
      } catch {
        /* ignored */
      }
    },
    [isSupported, state.permission],
  )

  const showNotificationFromEvent = useCallback(
    async (event: ConversationEventPayload): Promise<void> => {
      if (!isSupported || state.permission !== 'granted') return

      const projectName = extractProjectName(event.eventMetadata.contextWorkDirectory)
      const body = `${event.hostDetails.hostName}\n${projectName}\n${event.notificationMessage}`

      await showNotification({
        title: 'C4',
        body,
        data: event,
      })
    },
    [isSupported, state.permission, showNotification],
  )

  const testNotification = useCallback(async (): Promise<void> => {
    if (!isSupported) return

    if (state.permission !== 'granted') {
      await requestPermission()
      return
    }

    await showNotification({
      title: 'C4',
      body: 'Test notification\nExample Project',
    })
  }, [isSupported, state.permission, requestPermission, showNotification])

  useEffect(() => {
    const handleConversationEvent = (event: CustomEvent): void => {
      const conversationEvent = event.detail as ConversationEventPayload

      if (
        !conversationEvent?.eventType ||
        conversationEvent.source === 'hydration' ||
        !BLOCKING_CONVERSATION_EVENT_TYPES_STRING.has(conversationEvent.eventType)
      ) {
        return
      }

      if (
        conversationEvent?.eventMetadata?.eventId &&
        conversationEvent?.notificationMessage &&
        conversationEvent?.eventMetadata?.timestamp
      ) {
        void showNotificationFromEvent(conversationEvent)
      }
    }

    globalThis.addEventListener(
      CLAUDE_CODE_CONVERSATION_EVENT,
      handleConversationEvent as EventListener,
    )

    return () => {
      globalThis.removeEventListener(
        CLAUDE_CODE_CONVERSATION_EVENT,
        handleConversationEvent as EventListener,
      )
    }
  }, [showNotificationFromEvent])

  useEffect(() => {
    if (!isSupported) return

    checkPermission()

    const interval = setInterval(checkPermission, env.NOTIFICATION_PERMISSION_CHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [checkPermission, isSupported])

  return {
    state: optimisticState,
    isPending,
    requestPermission,
    showNotification,
    testNotification,
    checkPermission,
  }
}
